import { clients } from "@/constants";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";

/* =========================
   데이터 매핑
========================= */
const featuredClientNames = [
  "신한은행",
  "국세청",
  "한국고용정보원",
  "kt powertel",
  "수협",
  "THE SHILLA",
  "SK broadband",
  "교보생명",
  "citibank",
  "E-LAND RETAIL",
  "KB생명",
];

const featuredClients = featuredClientNames
  .map(name => clients.flatMap(category => category.list).find(client => client.name === name))
  .filter((client): client is NonNullable<(typeof clients)[number]["list"][number]> =>
    Boolean(client)
  );

/* =========================
   5 / 6 분리
========================= */
const row1 = featuredClients.slice(0, 5);
const row2 = featuredClients.slice(5);

/* =========================
   width 측정 hook (핵심)
========================= */
const useSliderWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.scrollWidth / 2);
    }
  }, []);

  return { ref, width };
};

/* =========================
   카드
========================= */
const ClientItem = ({ client }: { client: (typeof featuredClients)[number] }) => (
  <div className="flex min-w-[180px] items-center justify-center rounded-md border border-[#e5ebf7] bg-white px-8 py-2 shadow-[0_8px_24px_rgba(34,68,129,0.05)]">
    <img
      src={`/img/about/${client.img}`}
      alt={client.name}
      className="max-h-[50px] object-contain opacity-90"
      loading="lazy"
    />
  </div>
);

/* =========================
   메인
========================= */
const MainClientsSection = () => {
  const { ref: topRef, width: topWidth } = useSliderWidth();
  const { ref: bottomRef, width: bottomWidth } = useSliderWidth();

  return (
    <>
      <section className="pt-12 overflow-hidden bg-white sm:pt-18 sm:pb-24">
        <div className="px-[2rem] mx-auto text-center max-w-container sm:px-8">
          {/* 텍스트 */}
          <div>
            <h2 className="text-[3rem] font-extrabold leading-[1.3] tracking-[-0.04em] text-[#242a38] sm:text-[4.8rem]">
              25년의 시간이 증명하는 <br className="md:hidden" />
              <span className="text-std-blue">통신 서비스의 기준</span>
            </h2>
            <p className="mt-5 text-[2rem] leading-[1.8] text-[#66748c] sm:text-[1.8rem]">
              대한민국 메시징 시장을 최초로 개척한 25년의 노하우로, 인도적인 품질과 시스템 안정성을
              보장합니다.
            </p>
            <p className="mt-8 text-[2rem] font-medium text-[#66748c]">
              이미 많은 기업이 <span className="text-std-blue">스탠다드네트웍스</span>를
              선택했습니다
            </p>
          </div>
          {/* =========================
             로고 영역
          ========================= */}
          <div className="mt-12 space-y-6">
            <div
              className="mx-auto overflow-hidden"
              style={{
                maxWidth: "1080px",
                maskImage:
                  "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
              }}
            >
              <motion.div
                ref={topRef}
                className="flex gap-6 w-max"
                animate={topWidth ? { x: [-topWidth, 0] } : {}}
                transition={{
                  repeat: Infinity,
                  duration: 26,
                  ease: "linear",
                }}
              >
                {[...row1, ...row1].map((client, idx) => (
                  <ClientItem key={`top-${idx}`} client={client} />
                ))}
              </motion.div>
            </div>
            {/* ===== 아래 (6개 슬라이드) ===== */}
            <div
              className="mx-auto overflow-hidden"
              style={{
                maxWidth: "1320px",
                maskImage:
                  "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
              }}
            >
              <motion.div
                ref={bottomRef}
                className="flex gap-6 w-max"
                animate={bottomWidth ? { x: [0, -bottomWidth] } : {}}
                transition={{
                  repeat: Infinity,
                  duration: 32,
                  ease: "linear",
                }}
              >
                {[...row2, ...row2].map((client, idx) => (
                  <ClientItem key={`bottom-${idx}`} client={client} />
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full  px-6 py-[5rem] bg-[#2563EB] text-center shadow-lg">
        <div className="mx-auto max-w-container flex flex-col items-center justify-center gap-10">
          <h2 className="text-white font-bold text-[2.8rem] md:text-[3.6rem] leading-[1.4] tracking-tight break-keep">
            번거로운 비즈니스 업무, 이제 스탠다드네트웍스를 통해
            <br />
            지금 바로 시작해 보세요
          </h2>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-2 w-full max-w-md sm:max-w-none mx-auto">
            <a
              href="https://a015.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-[#ffffff] hover:bg-gray-200 text-std-black text-[1.6rem] md:text-[1.8rem] font-bold px-10 py-5 rounded-md shadow-sm transition-colors text-center"
            >
              문자콜센터 가입하기
            </a>

            <a
              href="/service/submit"
              className="w-full sm:w-auto bg-transparent hover:bg-white text-white hover:text-std-black text-[1.6rem] md:text-[1.8rem] font-bold px-10 py-5 rounded-md border-2 border-white/80 flex items-center justify-center gap-2 transition-all duration-300 text-center"
            >
              <span>메시징 서비스 신청하기</span>
              <span className="material-icons-round text-[1.8rem] leading-none">
                arrow_forward_ios
              </span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default MainClientsSection;
